import React from 'react';
import { RING } from '../surface';
import { resolveScheme, inkOn, contrast, bandBg, bandFg, PORTAL_THEME } from '../../utils/emailTheme';

/**
 * The email, drawn the way templates C (admin newsletter) and D (Powered by Portal) draw it —
 * `docs/email-templates/brevo/`, the Email Render prototype. Same block schema, same resolver
 * (`utils/emailTheme`) as the server, so the colours here are the colours in the inbox.
 *
 * `page` set = template D (the page's logo + name on top, its list and POWERED BY PORTAL at the
 * bottom); null = template C (the Portal wordmark). `scheme` is { primary, accent, canvas } or null
 * for PORTAL. `events` maps eventId → the picker row (name, image, when, where, price, capacity,
 * status, statusColor). `onSelect(key)` makes every block a button; `activeKey` rings one.
 */
const MONO = "'JetBrains Mono','Courier New',monospace";
const SANS = "'Helvetica Neue', Arial, sans-serif";

export default function EmailPreview({
  meta, blocks = [], page = null, scheme = null, showName = true, phone = false, events = {}, activeKey = null, onSelect,
}) {
  const t = page ? resolveScheme(scheme) : PORTAL_THEME;
  const canvasDark = t.canvasInk === '#FFFFFF';
  const selectable = typeof onSelect === 'function';
  const cardPad = phone ? 22 : 28;
  const headline = (meta.title || meta.subject || 'HEADLINE');
  const label = meta.issueLabel ? meta.issueLabel.toUpperCase() : '';
  const name = (page?.name || '').toUpperCase();
  const logo = canvasDark ? '/email/portal-logo-white.png' : '/email/portal-logo-black.png';

  return (
    <div
      data-testid="email-preview"
      style={{ width: phone ? 375 : 600, maxWidth: '100%', margin: '0 auto', background: t.canvas, padding: phone ? '16px 12px 20px' : '24px 20px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12, fontFamily: SANS, color: '#0A0A0A' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '2px 4px 4px' }}>
        {page ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {page.logoUrl
              ? <img src={page.logoUrl} alt={page.name} style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', display: 'block', flexShrink: 0 }} />
              : <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 12, background: '#0A0A0A', flexShrink: 0 }} />}
            {showName && <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 800, letterSpacing: '.14em', color: t.canvasInk, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>}
          </div>
        ) : (
          <img src={logo} alt="Portal" style={{ height: 24, width: 'auto', display: 'block' }} />
        )}
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.16em', color: t.canvasMuted, textDecoration: 'underline', whiteSpace: 'nowrap' }}>VIEW IN BROWSER</span>
      </div>

      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 24, background: t.primary, color: t.primaryInk, padding: phone ? '30px 22px 28px' : '40px 36px 36px' }}>
        {label && (
          <span
            data-testid="email-label"
            style={{
              display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 14px', boxSizing: 'border-box', borderRadius: 999,
              border: `2px solid ${!page ? 'transparent' : t.accent}`,
              background: !page ? `linear-gradient(${t.primary},${t.primary}) padding-box, ${RING} border-box` : t.primary,
              color: t.primaryInk, fontFamily: MONO, fontSize: 10, fontWeight: 800, letterSpacing: '.18em',
            }}
          >
            {label}
          </span>
        )}
        <div style={{ marginTop: 14, fontFamily: MONO, fontSize: phone ? 24 : 30, lineHeight: 1.18, fontWeight: 800, letterSpacing: '.02em', textTransform: 'uppercase', overflowWrap: 'anywhere' }}>{headline}</div>
        {meta.intro && <div style={{ marginTop: 14, fontSize: 16, lineHeight: '26px', color: t.primarySoft }}>{meta.intro}</div>}
      </div>

      {blocks.map((b) => (
        <PreviewBlock key={b.key} b={b} t={t} phone={phone} cardPad={cardPad} ev={b.type === 'event' ? events[b.eventId] : null}
          active={selectable && activeKey === b.key} onSelect={selectable ? () => onSelect(b.key) : null} />
      ))}

      {blocks.length === 0 && (
        <div style={{ borderRadius: 24, border: `2px dashed ${canvasDark ? '#3A3A3A' : '#CFCFCF'}`, padding: '36px 20px', textAlign: 'center', fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '.16em', color: t.canvasMuted }}>ADD A BLOCK TO START</div>
      )}

      {(meta.ctaText || meta.ctaUrl) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 56, borderRadius: 999, background: t.primary, color: t.primaryInk, fontFamily: MONO, fontSize: 13, fontWeight: 800, letterSpacing: '.2em', textTransform: 'uppercase' }}>
          {meta.ctaText || 'OPEN'}
        </div>
      )}

      <div style={{ padding: '22px 12px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', fontFamily: MONO, fontSize: 10, lineHeight: '18px', letterSpacing: '.14em', color: t.canvasMuted }}>
        {page ? (
          <>
            <span>YOU ARE GETTING THIS BECAUSE YOU ARE ON {name}&rsquo;S LIST.</span>
            <span style={{ textDecoration: 'underline', color: t.canvasInk }}>STOP GETTING EMAIL FROM {name}</span>
            <span>{(page.address || 'PORTAL INC. · 2840 NOYES RD. NARAMATA, BC V0H 1N1').toUpperCase()}</span>
            <img src="/email/powered-by-portal.png" alt="Powered by Portal" style={{ marginTop: 10, width: 176, height: 38, display: 'block' }} />
          </>
        ) : (
          <>
            <img src={logo} alt="Portal" style={{ height: 20, width: 'auto', display: 'block', opacity: 0.9 }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 18px', color: t.canvasInk }}>
              <span style={{ textDecoration: 'underline' }}>DOWNLOAD THE APP</span><span style={{ textDecoration: 'underline' }}>CONTACT US</span><span style={{ textDecoration: 'underline' }}>UNSUBSCRIBE</span>
            </div>
            <span>YOU ARE GETTING THIS BECAUSE YOU HAVE A PORTAL ACCOUNT.</span>
            <span>2840 NOYES RD. NARAMATA, BC V0H 1N1</span>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewBlock({ b, t, phone, cardPad, ev, active, onSelect }) {
  const [hover, setHover] = React.useState(false);
  let bg = '#FFFFFF';
  if (b.type === 'band') bg = bandBg(b.tone, t);
  const ink = b.type === 'band' ? bandFg(b.ink, bg, t) : '#0A0A0A';
  const ring = active ? '0 0 0 3px #22ECFB' : hover && onSelect ? '0 0 0 2px rgba(34,236,251,.6)' : 'none';
  const sel = onSelect ? {
    role: 'button', tabIndex: 0, onClick: (e) => { e.stopPropagation(); onSelect(); },
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } },
    onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false),
    'aria-label': `Edit ${b.type} block`,
  } : {};
  const link = (text) => text && (
    <span style={{ alignSelf: 'flex-start', marginTop: 8, fontFamily: MONO, fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textDecoration: 'underline', color: t.link, textTransform: 'uppercase' }}>{text} →</span>
  );
  const body = (text) => <div style={{ fontSize: 15, lineHeight: '24px', color: '#333', whiteSpace: 'pre-line' }}>{text || 'Write something here.'}</div>;
  const img = (src, alt, style) => (src
    ? <img src={src} alt={alt || ''} style={{ height: 'auto', display: 'block', borderRadius: 16, background: '#EDEDED', ...style }} />
    : <div style={{ aspectRatio: '4 / 3', borderRadius: 16, background: '#EDEDED', ...style }} />);

  let inner = null;
  if (b.type === 'text') {
    inner = (
      <div style={{ padding: cardPad, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {b.heading && <div style={{ fontSize: 20, lineHeight: '27px', fontWeight: 700 }}>{b.heading}</div>}
        {body(b.body)}
      </div>
    );
  } else if (b.type === 'imgtext') {
    inner = (
      <div style={{ display: 'flex', flexDirection: phone ? 'column' : b.side === 'right' ? 'row-reverse' : 'row', gap: 20, padding: cardPad, alignItems: 'flex-start' }}>
        {img(b.img, b.alt, { width: phone ? '100%' : 200, flexShrink: 0 })}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {b.heading && <div style={{ fontSize: 20, lineHeight: '27px', fontWeight: 700 }}>{b.heading}</div>}
          {body(b.body)}
          {b.url && link(b.link || 'READ MORE')}
        </div>
      </div>
    );
  } else if (b.type === 'photo') {
    inner = (
      <>
        <div style={{ padding: 12 }}>{img(b.img, b.alt, { width: '100%' })}</div>
        {b.caption && <div style={{ padding: '2px 22px 18px', fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', color: '#6B6B6B', textTransform: 'uppercase' }}>{b.caption}</div>}
      </>
    );
  } else if (b.type === 'band') {
    inner = (
      <div style={{ padding: '26px 28px' }}>
        <div style={{ fontFamily: MONO, fontSize: 18, lineHeight: '26px', fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{b.heading || 'SECTION'}</div>
        {b.body && <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 11, lineHeight: '19px', letterSpacing: '.12em', textTransform: 'uppercase', opacity: 0.8 }}>{b.body}</div>}
      </div>
    );
  } else if (b.type === 'event') {
    const sBg = ev?.statusColor || '#FFFFFF';
    inner = (
      <div style={{ display: 'flex', flexDirection: phone ? 'column' : 'row', gap: 16, padding: 16 }}>
        {img(ev?.image, ev?.name, { width: phone ? '100%' : 170, flexShrink: 0, alignSelf: 'flex-start' })}
        <div style={{ flex: 1, minWidth: 0, padding: '4px 6px 4px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ev?.status && <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 10px', borderRadius: 999, background: sBg, color: inkOn(sBg), fontFamily: MONO, fontSize: 9, fontWeight: 800, letterSpacing: '.16em' }}>{ev.status}</span>}
          <div style={{ fontSize: 18, lineHeight: '24px', fontWeight: 700 }}>{ev?.name || 'Pick an event'}</div>
          {ev?.when && <Meta label="WHEN" value={ev.when} />}
          {ev?.where && <Meta label="WHERE" value={ev.where} />}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 12, borderTop: '1px solid #EDEDED', fontFamily: MONO }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>{ev?.price || 'FREE'}</span>
            <span style={{ fontSize: 10, letterSpacing: '.12em', color: '#6B6B6B' }}>{ev?.capacity || ''}</span>
          </div>
          <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', height: 36, padding: '0 18px', borderRadius: 999, background: t.primary, color: t.primaryInk, fontFamily: MONO, fontSize: 10, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase' }}>{b.cta || 'GET TICKETS'}</span>
        </div>
      </div>
    );
  } else if (b.type === 'button') {
    const filled = b.style !== 'outline';
    inner = (
      <div style={{ padding: '22px 24px', display: 'flex', justifyContent: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 48, padding: '0 30px', borderRadius: 999, background: filled ? t.primary : '#FFFFFF', color: filled ? t.primaryInk : '#0A0A0A', boxShadow: filled ? 'none' : 'inset 0 0 0 2px #0A0A0A', fontFamily: MONO, fontSize: 12, fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase' }}>{b.label || 'OPEN'}</span>
      </div>
    );
  }

  return (
    <div data-testid={`preview-block-${b.key}`} data-active={active ? 'true' : undefined} {...sel}
      style={{ position: 'relative', borderRadius: 24, background: bg, color: ink, overflow: 'hidden', cursor: onSelect ? 'pointer' : 'default', boxShadow: ring, transition: 'box-shadow .15s', outline: 'none' }}>
      {inner}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontFamily: MONO }}>
      <span style={{ fontSize: 9, letterSpacing: '.18em', color: '#6B6B6B' }}>{label}</span>
      <span style={{ fontSize: 12 }}>{value}</span>
    </div>
  );
}

/** Exported for the band editor's contrast warning. */
export const bandReadable = (b, scheme) => {
  const t = resolveScheme(scheme);
  if (!b.ink || b.ink === 'auto') return true;
  const bg = bandBg(b.tone, t);
  return contrast(bg, bandFg(b.ink, bg, t)) >= 3;
};
